import { ICache } from '../interfaces';

/**
 * A simple memory cache implementation.
 */
export class MemoryCache implements ICache {

  private _cache = new Set();

  /**
   * Gets the value from the cache if exists, otherwise from the provided getter method and puts
   * that value in the cache.
   * 
   * @param key     The cache key to use.
   * @param getter  The value getter if cache miss.
   * @returns       The value from cache, or from the getter.
   */
  getOrSet<T>(key: string, getter: () => T) {
    if (key in this._cache) {
      return this._cache[key];
    }

    const value = getter();
    this._cache[key] = value;
    return value;
  }

  /**
   * Gets a promise from the cache if exists, otherwise puts the promise itself in the cache
   * to be shared between callers.
   * 
   * @param key     The cache key to use.
   * @param getter  The promise getter if cache miss.
   * @returns       The promise from cache, or from the getter.
   */
  getOrSetAsync<T>(key: string, getter: () => Promise<T>): Promise<T> {
    if (key in this._cache) {
      return this._cache[key];
    }

    const promise = getter();
    // Cache the promise itself to prevent parallel executions, for example when multiple parts request the same
    // api parallel which could take some time to resolve.
    this._cache[key] = promise;
    return promise;
  }
}
